<template>
   <div>
      <h3 class="section-title" id="states">State Appointment &amp; Fees</h3>

      <p class="instructions">
         Fees, if they exist, may be different for residents and non-residents. Please ensure you've entered your home
         address on the "personal information" section so your residence can be confirmed.
      </p>

      <section-bar>
         Selected States to be Appointed In
      </section-bar>

      <table class="table table-condensed table-striped font-smaller">
         <thead>
            <tr>
               <th>Select State to Add</th>
               <th class="text-right">Fees</th>
               <th class="text-center" v-if="$parent.readOnly()==null">Action</th>
            </tr>
            <tr v-for="itm in stateList" :key="itm.stateId">
               <td>{{itm.stateName}}</td>
               <td class="text-right">$ {{itm.feeAmount==null ? 0 : itm.feeAmount | twoplaces}}</td>
               <td class="text-center" v-if="$parent.readOnly()==null">
                  <button type="button" class="ml-red ml-h-black" v-tooltip:right="'Delete State'" @click.prevent="deleteState(itm)">
                     <i class="fas fa-times"></i>
                  </button>
               </td>
            </tr>
         </thead>
         <tbody>

            <tr v-if="showAddRow && $parent.readOnly()==null">
               <td>
                  <!-- Custom dropdown with inline search so typing filters options across browsers -->
                  <div class="add-state-btn custom-dd" ref="feesDropdown">
                     <button type="button" class="btn the-button"
                             @click.prevent="toggleFeesMenu"
                             @keydown="onButtonKeydown"
                             @focus="onButtonFocus"
                             @mousedown="onButtonMouseDown"
                             :class="{ 'error--input': showAddRow && v$.states && v$.states.$error }">
                        <span class="fas fa-caret-down"></span> {{ stateText() }}
                     </button>

                     <div v-if="showFeesMenu" class="fees-menu shadow">
                        <div class="fees-search">
                           <input ref="feesSearch" type="text" v-model="stateSearch" @input="onStateSearch" @keydown="onSearchKeydown" class="form-control" placeholder="Type to filter states..." />
                        </div>
                        <ul class="list-unstyled fees-list">
                           <li v-for="(itm, index) in filteredFees" :key="itm.stateFeeId" class="fees-item">
                              <a href="#" class="dropdown-item" tabindex="0" role="button"
                                 @mousedown.prevent="selectFeeFromMenu(itm)"
                                 @keydown.enter.prevent="selectFeeFromMenu(itm)"
                                 @keydown.space.prevent="selectFeeFromMenu(itm)"
                                 @keydown="onItemKeydown">
                                 <div class="row">
                                    <div class="col-12 col-md-6 state-name-wrap">{{ itm.stateName }}</div>
                                    <div class="col-12 col-md-3 text-right">{{ itm.residentFeeAmount | currency }}</div>
                                    <div class="col-12 col-md-3 text-right">{{ itm.nonResidentFeeAmount | currency }}</div>
                                 </div>
                              </a>
                              <div class="dropdown-divider" v-if="index != filteredFees.length-1"></div>
                           </li>
                           <li v-if="filteredFees.length === 0" class="text-muted p-2">No states match.</li>
                        </ul>
                     </div>
                  </div>

                  <!-- show add-row validation for missing selection -->
                  <div class="error--msg" v-if="v$ && v$.states && v$.states.$invalid">
                     {{ $parent.errorText(v$.states) || 'At least one State Fee must be chosen' }}
                  </div>

               </td>
               <td class="text-right">$ {{selectedFee() | currency}}</td>
               <td class="text-center">
                  <!-- cancel button: add ref so we can focus it -->
                  <button type="button" class="ml-red ml-h-black" v-tooltip:right="'Cancel'" @click.prevent="abortAdd" ref="abortBtn">
                     <i class="fas fa-times"></i>
                  </button>
               </td>
            </tr>
         </tbody>
         <tfoot>
            <tr>
               <td colspan="2" class="text-right font-smaller">
                  Total Amount = $ {{totalFees | twoplaces}}
               </td>
            </tr>
         </tfoot>
      </table>

      <p v-if="residentState!= ''">Your resident State Is '{{residentState}}'</p>

      <div class="alert alert-warning" role="alert">
         <button type="button" class="close" data-dismiss="alert" aria-label="close">
            <span aria-hidden="true">&times;</span>
         </button>
         <i class="fas fa-exclamation-triangle"></i>
         Please Note: We do not charge appointment fees until the first piece of business is submitted in that state, except for Pre-Appointment states.
      </div>
      <table v-if="stateFeeQuestion" class="table table-condensed question-table">
         <tr>
            <td>
               {{stateFeeQuestion.questionText}}
               <div class="error--msg" v-if="!stateFeeAnswered()">
                  State Fee Question is Required
               </div>
            </td>
            <td class="yn-col">
               <div class="custom-control custom-radio custom-control-inline">
                  <input type="radio" :id="'rad'+stateFeeQuestion.questionId" :name="'ans'+stateFeeQuestion.questionId"
                         class="custom-control-input" :value="true" @change="v$.answers.$touch(); chg();"
                         v-model="getAnswer(stateFeeQuestion.questionId).answerBoolean">
                  <label class="custom-control-label" :for="'rad'+stateFeeQuestion.questionId">Yes</label>
               </div>
               <div class="custom-control custom-radio custom-control-inline">
                  <input type="radio" :id="'radN'+stateFeeQuestion.questionId" :name="'ans'+stateFeeQuestion.questionId"
                         class="custom-control-input" :value="false" @change="v$.answers.$touch(); chg();"
                         v-model="getAnswer(stateFeeQuestion.questionId).answerBoolean">
                  <label class="custom-control-label" :for="'radN'+stateFeeQuestion.questionId">No</label>
               </div>
            </td>
         </tr>
         <tr v-if="getAnswer(stateFeeQuestion.questionId).answerBoolean">
            <td>
               <div class="custom-control custom-checkbox">
                  <input type="checkbox" class="custom-control-input" id="PA15" v-model="isPAFee" @change="chg();">
                  <label class="custom-control-label" for="PA15">PA - $ 15.00</label>
               </div>
               <div class="custom-control custom-checkbox">
                  <input type="checkbox" class="custom-control-input" id="MT0" v-model="isMTFee" @change="chg();">
                  <label class="custom-control-label" for="MT0">MT - $ 0.00</label>
               </div>
               <div class="error--msg" v-if="!stateFeeValid()">
                  At least one State Fee needs to be selected
               </div>
            </td>
         </tr>
         <tr v-if="getAnswer(stateFeeQuestion.questionId).answerBoolean">
            <td>
               Please note:  The appointment fee for PA is non-refundable.
            </td>
         </tr>
      </table>

      <table v-if="puertoRicoQuestion" class="table table-condensed question-table">
         <tr>
            <td>
               {{puertoRicoQuestion.questionText}}
            </td>
            <td class="yn-col">
               <div class="custom-control custom-radio custom-control-inline">
                  <input type="radio" :id="'rad'+puertoRicoQuestion.questionId" :name="'ans'+puertoRicoQuestion.questionId"
                         class="custom-control-input" :value="true" @change="v$.answers.$touch(); chg();"
                         v-model="getPRAnswer(puertoRicoQuestion.questionId).answerBoolean">
                  <label class="custom-control-label" :for="'rad'+puertoRicoQuestion.questionId">Yes</label>
               </div>
               <div class="custom-control custom-radio custom-control-inline">
                  <input type="radio" :id="'radN'+puertoRicoQuestion.questionId" :name="'ans'+puertoRicoQuestion.questionId"
                         class="custom-control-input" :value="false" @change="v$.answers.$touch(); chg();"
                         v-model="getPRAnswer(puertoRicoQuestion.questionId).answerBoolean">
                  <label class="custom-control-label" :for="'radN'+puertoRicoQuestion.questionId">No</label>
               </div>
            </td>
         </tr>
         <tr v-if="getPRAnswer(puertoRicoQuestion.questionId).answerBoolean">
            <td>
               {{puertoRico2ndQuestion.questionText}}
            </td>
            <td class="yn-col">
               <div class="custom-control custom-radio custom-control-inline">
                  <input type="radio" :id="'rad'+puertoRico2ndQuestion.questionId" :name="'ans'+puertoRico2ndQuestion.questionId"
                         class="custom-control-input" :value="true" @change="v$.answers.$touch(); chg();"
                         v-model="getPRAnswer(puertoRico2ndQuestion.questionId).answerBoolean">
                  <label class="custom-control-label" :for="'rad'+puertoRico2ndQuestion.questionId">Yes</label>
               </div>
               <div class="custom-control custom-radio custom-control-inline">
                  <input type="radio" :id="'radN'+puertoRico2ndQuestion.questionId" :name="'ans'+puertoRico2ndQuestion.questionId"
                         class="custom-control-input" :value="false" @change="v$.answers.$touch(); chg();"
                         v-model="getPRAnswer(puertoRico2ndQuestion.questionId).answerBoolean">
                  <label class="custom-control-label" :for="'radN'+puertoRico2ndQuestion.questionId">No</label>
               </div>
            </td>
         </tr>
      </table>

      <fieldset class="border p-2 marb-12" v-if="showType(['R'])">
         <legend class="w-auto">Pay Fees By Credit Card</legend>

         <div class="form-group row">
            <label for="creditCardTypeCode" class="col-sm-2 col-form-label" :class="{ 'error--label': v$.ccInfo.creditCardTypeCode.$error }">Credit Card Type</label>
            <div class="col-sm-4">
               <select class="form-control" id="ccInfo.creditCardTypeCode" :disabled="$parent.readOnly()" @blur="v$.ccInfo.creditCardTypeCode.$touch"
                       v-model="ccInfo.creditCardTypeCode" @change="chg" :class="{ 'error--input': v$.ccInfo.creditCardTypeCode.$error }">
                  <option :value="null" disabled hidden>(card type)</option>
                  <option value="CCTYP_MASTER">MasterCard</option>
                  <option value="CCTYP_VISA">Visa</option>
                  <option value="CCTYP_OTHER">Other</option>
               </select>
               <div class="error--msg" v-if="v$.ccInfo.creditCardTypeCode.$error">
                  {{ $parent.errorText(v$.ccInfo.creditCardTypeCode) }}
               </div>
            </div>
            <field-col name="ccInfo.cardNumber" label="Credit Card Number" v-model="ccInfo.cardNumber" :v$="v$.ccInfo.cardNumber" @change="chg" lcols="2" icols="4"></field-col>
         </div>
         <div class="form-group row">
            <label for="expirationMonth" class="col-sm-2 col-form-label" :class="{ 'error--label': v$.ccInfo.creditCardTypeCode.$error }">Expiration Month/Year</label>
            <div class="col-sm-4">
               <select class="form-control" style="width: 90px !important; display: inline-block !important" aria-placeholder="(month)" id="ccInfo.expirationMonth"
                       :disabled="$parent.readOnly()" @blur="v$.ccInfo.expirationMonth.$touch"
                       v-model.number="ccInfo.expirationMonth" @change="chg" :class="{ 'error--input': v$.ccInfo.expirationMonth.$error }">
                  <option :value="null" disabled hidden>(MM)</option>
                  <option v-for="m in expirationMonths()" :key="m" :value="m">{{(m).toString().padStart(2, "0")}}</option>
               </select>&nbsp;
               <select class="form-control" style="width: 130px !important; display: inline-block !important" id="expirationYear"
                       :disabled="$parent.readOnly()" @blur="v$.ccInfo.expirationYear.$touch"
                       v-model="ccInfo.expirationYear" @change="chg" :class="{ 'error--input': v$.ccInfo.expirationYear.$error }">
                  <option :value="null" disabled hidden>(YYYY)</option>
                  <option v-for="y in expirationYears()" :key="y" :value="y">{{y}}</option>
               </select>
               <div class="error--msg" v-if="v$.ccInfo.expirationMonth.$error || v$.ccInfo.expirationYear.$error">
                  {{ $parent.errorText(v$.ccInfo.expirationMonth) }} {{ $parent.errorText(v$.ccInfo.expirationYear) }}
               </div>
            </div>
            <field-col name="ccInfo.cvcNumber" label="CVC Number" v-model="ccInfo.cvcNumber" :v$="v$.ccInfo.cvcNumber" @change="chg" lcols="2" icols="4"></field-col>
         </div>
         <div class="form-group row">
            <field-col name="ccInfo.nameonCard" label="Name On Card" v-model="ccInfo.nameonCard" :v$="v$.ccInfo.nameonCard" @change="chg" lcols="2" icols="8"></field-col>
         </div>
         <div class="form-group row">
            <field-col name="ccInfo.billingAddress" label="Billing Address" v-model="ccInfo.billingAddress" :v$="v$.ccInfo.billingAddress" @change="chg" lcols="2" icols="4"></field-col>
            <field-col name="ccInfo.city" label="City" v-model="ccInfo.city" :v$="v$.ccInfo.city" @change="chg" lcols="2" icols="4"></field-col>
         </div>
         <div class="form-group row">
            <label for="ccInfo.stateCode" class="col-sm-2 col-form-label" :class="{ 'error--label': v$.ccInfo.stateCode.$error }">State</label>
            <div class="col-sm-4">
               <!-- Use an input + datalist so typing 'T' filters to Tennessee & Texas -->
               <div class="cc-state-wrapper">
                     <input type="text"
                            class="form-control"
                            id="ccInfo.stateCode"
                            placeholder="(choose a state)"
                            :disabled="$parent.allowApprovedEdit() ? false : true"
                            v-model="ccStateSearch"
                            @input="onStateInput"
                            @blur="onStateBlur"
                            list="statesDL"
                            :class="{ 'error--input': v$.ccInfo.stateCode.$error }" />

                     <datalist id="statesDL">
                        <option v-for="s in allStates" :key="s.code" :value="s.name"></option>
                     </datalist>

                     <div class="error--msg" v-if="v$.ccInfo.stateCode.$error">
                        {{ $parent.errorText(v$.ccInfo.stateCode) }}
                     </div>
                  </div>
               </div>
               <field-col name="ccInfo.zip" label="Zip Code" v-model="ccInfo.zip" :v$="v$.ccInfo.zip" @change="chg" lcols="2" icols="4"></field-col>
            </div>
      </fieldset>

      <p v-else><strong>Appointment Fees, if any, will be deducted from Commissions.</strong></p>
      <section-bar>Applicant's Signature</section-bar>
      <p class="instructions">Please enter your mother's maiden name indicating agreement with the above fees.</p>
      <div class="form-group row" v-if="!gettingSigs && v$.signatures && v$.signatures.hasSigFees">
         <label for="feeMmName" class="col-sm-4 col-form-label"
                :class="{ 'error--label': v$.signatures.hasSigFees.$error }">Mother's Maiden Name</label>
         <div class="col-sm-4" v-if="v$.signatures && v$.signatures.hasSigFees">
            <input type="text" class="form-control" id="feeMmName" placeholder="(mother's maiden name)" :readonly="$parent.readOnly()"
                   v-model="signatures[feesig].signatureValue" @change="chg" @blur="v$.signatures.$touch()"
                   :class="{ 'error--input': v$.signatures.$dirty && v$.signatures.hasSigFees.$invalid }">
            <div class="error--msg" v-if="v$.signatures.$dirty && v$.signatures.hasSigFees.$invalid">
               {{ v$.signatures.hasSigFees.$message }}
            </div>
         </div>
      </div>

   </div>
</template>

<script>
import FieldCol from "../fieldCol.vue";

export default {
   name: "comp",
   components: {
      'field-col': FieldCol
   },
   props: { 
      'residentState': String,
      'apptType': String,
      'apptId': Number,
      'sigs': Array,
      'v$': Object,
      'ccardInfo': Object,
      'states': Array,
      'answers': Array,
      'isPA': Boolean,
      'isMT': Boolean,
      'allowPuertoRico': {
         type: Boolean,
         default: true
      }
   },
   data() {
      return {
         // Always show a row for adding states on initial load (editable only in template)
         showAddRow: true,
         selectedStateId: null,
         totalFees: 0.00,
         fees: [],
         selectedState: null,
         signatures: [],
         stateList: [],
         gettingSigs: true,
         feesig: null,
         ccInfo: {},
         stateFeeQuestion: {},
         puertoRicoQuestion: {},
         puertoRico2ndQuestion: {}, 
         answerCol: [],
         isPAFee: false,
         isMTFee: false,
         suppressFocusOpen: false,
         // datalist driven state input
         ccStateSearch: '',
         allStates: [
            { code: "AA", name: "Asia" },
            { code: "AK", name: "Alaskafff" },
            { code: "AL", name: "Alabama" },
            { code: "AR", name: "Arkansas" },
            { code: "AS", name: "American Samoa" },
            { code: "AZ", name: "Arizona" },
            { code: "BC", name: "British Columbia" },
            { code: "CA", name: "California" },
            { code: "CM", name: "Northern Mariana Islands" },
            { code: "CN", name: "Canada" },
            { code: "CO", name: "Colorado" },
            { code: "CT", name: "Connecticut" },
            { code: "DC", name: "District of Columbia" },
            { code: "DE", name: "Delaware" },
            { code: "FL", name: "Florida" },
            { code: "GA", name: "Georgia" },
            { code: "GU", name: "Guam" },
            { code: "HI", name: "Hawaii" },
            { code: "IA", name: "Iowa" },
            { code: "ID", name: "Idaho" },
            { code: "IL", name: "Illinois" },
            { code: "IN", name: "Indiana" },
            { code: "KS", name: "Kansas" },
            { code: "KY", name: "Kentucky" },
            { code: "LA", name: "Louisiana" },
            { code: "LT", name: "Latin America" },
            { code: "MA", name: "Massachusetts" },
            { code: "MD", name: "Maryland" },
            { code: "ME", name: "Maine" },
            { code: "MI", name: "Michigan" },
            { code: "MN", name: "Minnesota" },
            { code: "MO", name: "Missouri" },
            { code: "MS", name: "Mississippi" },
            { code: "MT", name: "Montana" },
            { code: "NB", name: "New Brunswick" },
            { code: "NC", name: "North Carolina" },
            { code: "ND", name: "North Dakota" },
            { code: "NE", name: "Nebraska" },
            { code: "NH", name: "New Hampshire" },
            { code: "NJ", name: "New Jersey" },
            { code: "NM", name: "New Mexico" },
            { code: "NV", name: "Nevada" },
            { code: "NY", name: "New York" },
            { code: "OC", name: "Foreign Country" },
            { code: "OH", name: "Ohio" },
            { code: "OK", name: "Oklahoma" },
            { code: "OR", name: "Oregon" },
            { code: "PA", name: "Pennsylvania" },
            { code: "PR", name: "Puerto Rico", effectiveAllowPuertoRico: false },
            { code: "RI", name: "Rhode Island" },
            { code: "SC", name: "South Carolina" },
            { code: "SD", name: "South Dakota" },
            { code: "TN", name: "Tennessee" },
            { code: "TX", name: "Texas" },
            { code: "UT", name: "Utah" },
            { code: "VA", name: "Virginia" },
            { code: "VI", name: "Virgin Islands" },
            { code: "VT", name: "Vermont" },
            { code: "WA", name: "Washington" },
            { code: "WI", name: "Wisconsin" },
            { code: "WV", name: "West Virginia" },
            { code: "WY", name: "Wyoming" }
         ],

         // custom add-state dropdown state
         stateSearch: '',
         showFeesMenu: false
      };
   },
   watch: {
      // When effectiveAllowPuertoRico toggles to false, remove any PR from selected lists and ccInfo
      effectiveAllowPuertoRico(newVal) {
         if (!newVal) {
            this.removePuertoRicoSelections()
         }
      },

      // Watch answers collection; if primary Puerto Rico question is answered NO, force secondary NO and remove PR.
      answerCol: {
         handler() {
            // Ensure we have questions loaded
            if (!this.puertoRicoQuestion || !this.puertoRicoQuestion.questionId) return
            if (!this.puertoRico2ndQuestion || !this.puertoRico2ndQuestion.questionId) return

            const primary = this.getPRAnswer(this.puertoRicoQuestion.questionId)
            const secondary = this.getPRAnswer(this.puertoRico2ndQuestion.questionId)

            // If primary explicitly NO, set secondary to NO and remove PR selections/UI
            if (primary && primary.answerBoolean === false) {
               if (secondary && secondary.answerBoolean !== false) {
                  // set secondary answer to false
                  secondary.answerBoolean = false
               }
               // Remove PR from lists/UI and notify parent
               this.removePuertoRicoSelections()
               // Touch validation for answers/states so errors update
               if (this.v$ && this.v$.answers) this.v$.answers.$touch()
               if (this.v$ && this.v$.states) this.v$.states.$touch()
               // Emit update so parent gets the new answer state
               this.chg()
            }
         },
         deep: true
      }
   },
   async mounted() {
      this.$parent.$parent.loadingStart()
      this.gettingSigs = true
      this.fees = await this.$appoint.getFees()
      this.stateList = []
      this.states.forEach(i => { this.stateList.push(i) })

      // Remove any previously-selected Puerto Rico entries if not allowed
      if (!this.allowPuertoRico) {
         this.stateList = this.stateList.filter(s => {
            const name = (s.stateName || '').toLowerCase()
            const code = ((s.stateCode) ? s.stateCode : '').toUpperCase()
            return code !== 'PR' && name.indexOf('puerto') === -1
         })
      }

      this.calcFees()
      this.signatures = JSON.parse(JSON.stringify(this.sigs)) //deep copy
      this.storeSignatures()
      this.gettingSigs = false
      this.v$.signatures.$touch()
      if (this.apptType == "APPTTYP_REP" || this.apptType == "APPTTYP_PRIN")
      {
         if (this.ccardInfo == null)
         {
            this.ccInfo = {
               creditCardTypeCode : null,
               cardNumber : '',
               expirationMonth : null,
               expirationYear : null,
               nameonCard : '',
               billingAddress : '',
               city : '',
               stateCode : null,
               zip : ''
            }
         } else {
            this.ccInfo = JSON.parse(JSON.stringify(this.ccardInfo)) //deep copy
         }
      }

      // initialize the input from existing ccInfo
      if (this.ccInfo && this.ccInfo.stateCode) {
         let st = this.allStates.find(s => s.code === this.ccInfo.stateCode)
         if (st) this.ccStateSearch = st.name
      }
      await this.loadStateFeeQuestion();
      if (this.answers) {
         this.answerCol = JSON.parse(JSON.stringify(this.answers)) //deep copy
      }
      this.isPAFee = !!this.isPA;
      this.isMTFee = !!this.isMT;

      // ensure the add-row is visible only in editable mode
      this.showAddRow = (this.$parent.readOnly() == null)

      this.chg()
      this.$parent.$parent.loadingEnd()
      },
      beforeUnmount() {
         this.removeDocumentClick()
      },
      methods: {
         onButtonMouseDown() {
            // mark that mouse interaction started so focus handler won't auto-open
            this.suppressFocusOpen = true;
            // clear the flag shortly after to allow subsequent keyboard focus to open
            setTimeout(() => { this.suppressFocusOpen = false; }, 200);
         },

         onButtonFocus(evt) {
            // If the focus came as a result of a mousedown/click, ignore (mouse click toggles separately)
            if (this.suppressFocusOpen) {
               this.suppressFocusOpen = false;
               return;
            }

            // Open the menu and focus the search input for keyboard users
            if (!this.showFeesMenu) {
               this.showFeesMenu = true;
               this.$nextTick(() => {
                  const input = this.$refs.feesSearch;
                  if (input && typeof input.focus === 'function') input.focus();
                  this.addDocumentClick();
               });
            } else {
               // already open: ensure the search gets focus
               this.$nextTick(() => {
                  const input = this.$refs.feesSearch;
                  if (input && typeof input.focus === 'function') input.focus();
               });
            }
         },

         // handle keydown on the dropdown button so typing/Enter opens the menu and focuses the search input
         onButtonKeydown(evt) {
            // Escape closes menu and moves focus to Cancel
            if (evt.key === 'Escape' || evt.key === 'Esc') {
               evt.preventDefault();
               this.handleEscape();
               return;
            }

            // printable character => open and forward char to search so keyboard users can type immediately
            if (evt.key && evt.key.length === 1 && /\S/.test(evt.key)) {
               evt.preventDefault();
               if (!this.showFeesMenu) {
                  this.showFeesMenu = true;
                  this.$nextTick(() => {
                     this.stateSearch = evt.key;
                     const input = this.$refs.feesSearch;
                     if (input && input.focus) input.focus();
                     this.addDocumentClick();
                  });
               } else {
                  const input = this.$refs.feesSearch;
                  if (input && input.focus) input.focus();
               }
               return;
            }

            // Enter or Space should open the menu and focus
            if (evt.key === 'Enter' || evt.key === ' ') {
               evt.preventDefault();
               if (!this.showFeesMenu) {
                  this.toggleFeesMenu();
                  this.$nextTick(() => {
                     const input = this.$refs.feesSearch;
                     if (input && input.focus) input.focus();
                  });
               }
            }
         },


         // Focus the last delete (X) button in the states list (the row immediately above the add-row)
         focusPrevDeleteButton() {
            try {
               const tbl = this.$el && this.$el.querySelector('table');
               if (!tbl) return false;

               // look for the delete buttons inside the thead (those are the state rows)
               const delBtns = tbl.querySelectorAll('thead button.ml-red.ml-h-black');
               if (!delBtns || delBtns.length === 0) return false;

               // choose the last delete button (the row immediately above the add-row)
               const lastBtn = delBtns[delBtns.length - 1];
               if (lastBtn && typeof lastBtn.focus === 'function') {
                  lastBtn.focus();
                  return true;
               }
            } catch (e) {
               // defensive - ignore
            }
            return false;
         },

         // handle keydown on the inline search input (Esc closes menu & focuses Cancel; Shift+Tab focuses previous row X)
         onSearchKeydown(evt) {
            // Shift+Tab: move focus to previous row delete button
            if (evt.key === 'Tab' && evt.shiftKey) {
               // If the menu is open and there are previous rows, close menu and focus last delete button
               if (this.showFeesMenu) {
                  evt.preventDefault();
                  // clear partial selection (same behaviour as abort)
                  this.abortAdd();
                  // close menu
                  this.showFeesMenu = false;
                  this.removeDocumentClick();

                  // focus previous delete button; if not found, fall back to focusing the abort button
                  const focused = this.focusPrevDeleteButton();
                  if (!focused) {
                     this.$nextTick(() => {
                        const btn = this.$refs.abortBtn;
                        if (btn && typeof btn.focus === 'function') btn.focus();
                     });
                  }
               }
               return;
            }

            // Escape closes menu & focuses Cancel
            if (evt.key === 'Escape' || evt.key === 'Esc') {
               evt.preventDefault();
               this.handleEscape();
               return;
            }

            // allow other keys through (typing handled by v-model/onStateSearch)
         },

         // central escape behaviour: close menu, clear partial selection (same as Cancel) and focus the Cancel button
         handleEscape() {
            // Reuse abortAdd to clear selection and touch validation
            this.abortAdd();

            // Close menu defensively
            this.showFeesMenu = false;
            this.removeDocumentClick();

            // Move focus to the Cancel (X) button so keyboard users can continue tabbing
            this.$nextTick(() => {
               const btn = this.$refs.abortBtn;
               if (btn && typeof btn.focus === 'function') {
                  btn.focus();
               }
            });
         },


         // custom fees dropdown helpers
         toggleFeesMenu() {
            this.showFeesMenu = !this.showFeesMenu
            if (this.showFeesMenu) {
               this.$nextTick(() => {
                  const input = this.$refs.feesSearch
                  if (input && input.focus) input.focus()
                  this.addDocumentClick()
               })
            } else {
               // when closing via toggle, if there's a partial selection handle validation/add
               if (this.showAddRow) {
                  if (this.selectedState) {
                     this.addSelectedState()
                  } else {
                     if (this.v$ && this.v$.states) this.v$.states.$touch()
                  }
               }
               this.removeDocumentClick()
            }
         },
         onStateSearch() {
            // keep selectedState cleared while typing
            this.selectedState = null
         },
         // select from the menu: use addSelectedState(true) so we immediately open the next add-row and focus search
         selectFeeFromMenu(item) {
            // Set selected state (use fees collection to match shape)
            this.selectedState = this.fees.filter(obj => { return obj.stateId === item.stateId })[0]
            // Add and reopen for next entry
            this.addSelectedState(true)
            // maintain clean state for validation/chg
            this.stateSearch = ''
            this.chg()
         },
         handleDocumentClick(evt) {
            const el = this.$refs.feesDropdown
            // when clicking outside, if add-row visible and selection exists, add it; otherwise trigger validation
            if (!el) return
            if (!el.contains(evt.target)) {
               if (this.showAddRow) {
                  if (this.selectedState) {
                     this.addSelectedState()
                  } else {
                     if (this.v$ && this.v$.states) this.v$.states.$touch()
                  }
               }
               this.showFeesMenu = false
               this.removeDocumentClick()
            }
         },
         // Handle focus changes (keyboard navigation) - close menu if focus moved outside
         handleDocumentFocus(evt) {
            const el = this.$refs.feesDropdown
            if (!el) return
            const active = document.activeElement
            // If focus is still inside the dropdown, do nothing
            if (el.contains(active)) return

            // Focus moved outside -> same cleanup as clicking outside
            if (this.showAddRow) {
               if (this.selectedState) {
                  this.addSelectedState()
               } else {
                  if (this.v$ && this.v$.states) this.v$.states.$touch()
               }
            }
            this.showFeesMenu = false
            this.removeDocumentClick()
         },
         addDocumentClick() {
            // listen for outside mousedown and focus changes (focusin bubbles)
            document.addEventListener('mousedown', this.handleDocumentClick)
            document.addEventListener('focusin', this.handleDocumentFocus)
         },

         removeDocumentClick() {
            document.removeEventListener('mousedown', this.handleDocumentClick)
            document.removeEventListener('focusin', this.handleDocumentFocus)
         },
         expirationYears() {
            let yr = (new Date()).getFullYear()
            let arr = []
            for(let i=0; i<20; i++) {
               arr.push(yr + i)
            }
            return arr
         },
         expirationMonths() {
            let arr = []
            for(let i=1; i<13; i++) {
               arr.push(i)
            }
            return arr
         },
         // single method to add a selected state; `reopen` controls whether the menu re-opens for the next entry
         addSelectedState(reopen = true) {
            // If nothing selected, touch validation so the control will show red
            if (this.selectedState == null) {
               if (this.v$ && this.v$.states) this.v$.states.$touch()
               return
            }
            //Ensure state is not already configured
            let obj = this.stateList.filter(o => o.stateId == this.selectedState.stateId)
            if (obj && obj.length > 0) {
               this.$swal(
                  "Already Configured",
                  "Your selected state has already been added to the list.",
                  "warning"
               );
               // clear the selection so user can pick another
               this.selectedState = null
               this.stateSearch = ''
               return
            }

            // add record
            let newRec = {
               stateId: this.selectedState.stateId,
               stateName: this.selectedState.stateName,
               feeAmount: this.selectedFee()
            }
            this.stateList.push(newRec)

            // reset selection
            this.selectedState = null
            this.stateSearch = ''
            this.calcFees()
            if (this.v$ && this.v$.states) this.v$.states.$touch()
            this.chg()

            // If reopen requested, show the menu and focus the inline search for quick next entry
            if (reopen) {
               this.showFeesMenu = true
               this.$nextTick(() => {
                  const input = this.$refs.feesSearch
                  if (input && input.focus) input.focus()
                  // ensure document click handler active
                  this.addDocumentClick()
               })
            } else {
               // close and cleanup
               this.showFeesMenu = false
               this.removeDocumentClick()
            }
         },
         abortAdd() {
            // clear the current selection but keep an add row available
            this.selectedState = null
            this.stateSearch = ''
            this.showFeesMenu = false
            // mark states validation so UI reflects required state
            if (this.v$ && this.v$.states) this.v$.states.$touch()
            this.chg()
         },
         // handle keydown when a list item has focus (Esc closes & cancels)
         onItemKeydown(evt) {
            if (evt.key === 'Escape' || evt.key === 'Esc') {
               evt.preventDefault();
               // reuse existing escape logic: cancel selection, close menu, focus cancel button
               this.handleEscape();
               return;
            }

            // allow Enter/Space/Arrows to behave as before — other keys are handled elsewhere
         },

         deleteState(itm) {
            let indx = this.stateList.findIndex(item => item.stateId === itm.stateId)
            this.stateList.splice(indx, 1)
            this.calcFees()
            this.v$.states.$touch()
         },
         calcFees() {
            let tot = 0
            this.stateList.forEach(rec => {
               if (rec.feeAmount != null)
                  tot += rec.feeAmount
            });
            this.totalFees = tot
            this.chg()
         },
         stateText() {
            if (this.selectedState==null) return '(Choose A State)'
            return this.selectedState.stateName
         },
         selectedFee() {
            if (this.selectedState==null) return 0
            if (this.selectedState.stateCode==this.residentState)
               return this.selectedState.residentFeeAmount
            else
               return this.selectedState.nonResidentFeeAmount
         },
         handleStateChange(itm) {
            this.selectedState = this.fees.filter(obj => { return obj.stateId === itm.stateId })[0]
         },
         signatureByType(typ) {
            if (this.signatures === undefined) return null
            let i = null
            this.signatures.forEach((element, indx) => {
               if (element.signatureTypeCode == typ) {
                  i = indx
               }
            })
            return i
         },
         storeSignatures() {
            this.feesig = this.signatureByType('SIGTYP_ST_FEES')
            if (this.feesig==null) {
               let sig = {
                  appointmentId: this.apptId,
                  signatureTypeCode: 'SIGTYP_ST_FEES',
                  signatureValue: this.$store.getters.signature
               }
               this.feesig = this.signatures.length
               this.signatures.push(sig)
            } 
         },
         showType(tarr) {
            return this.$parent.showType(tarr, this.apptType)
         },
         // report the changes to the parent
         chg() {
            if (this.gettingSigs) return
            let rtn = { 
               states: this.stateList,
               signatures: this.signatures,
               ccInfo: this.ccInfo,
               showAddRow: this.showAddRow,
               answers: this.answerCol,
               isPA: this.isPAFee,
               isMT: this.isMTFee,
               // indicate whether the add-row currently has a selected (pending) state
               pendingState: !!this.selectedState
            }
            this.$emit('update', rtn)
         },
         async loadStateFeeQuestion() {
            await this.$appoint.getQuestions("QUES_TYP_STATE_FEE")
               .then(r => {
                  if (r) {
                     this.questions = r
                     this.stateFeeQuestion = r[0];
 
                  }
               })
            await this.$appoint.getQuestions("QUES_TYP_STATE_FEE_PR")
               .then(r => {
                  if (r) {
                     this.questions = r
                     this.puertoRicoQuestion = r[0]
                     this.puertoRico2ndQuestion = r[1]
                  }
               })
         },
         getAnswer(questionId) {
            let answer = this.answerCol.find(a => a.questionId == questionId)
            if (answer === undefined) {
               answer = {
                  answerBoolean: null,
                  answerText: null,
                  appointmentId: this.apptId,
                  questionId: questionId,
                  questionType: "QUES_TYP_STATE_FEE"
               }
               this.answerCol.push(answer)
            }
            if (answer.answerBoolean == false) {
               this.isPAFee = false;
               this.isMTFee = false;
            }
            return answer
         },
         getPRAnswer(questionId) {
            let answer = this.answerCol.find(a => a.questionId == questionId)
            if (answer === undefined) {
               answer = {
                  answerBoolean: null,
                  answerText: null,
                  appointmentId: this.apptId,
                  questionId: questionId,
                  questionType: "QUES_TYP_STATE_FEE_PR"
               }
               this.answerCol.push(answer)
            }
            return answer
         },

         // Centralized cleanup for removing Puerto Rico selections and UI state
         removePuertoRicoSelections() {
            // remove previously-selected Puerto Rico entries from stateList
            this.stateList = this.stateList.filter(s => {
               const code = (s.stateCode || '').toString().toUpperCase()
               const name = (s.stateName || '').toString().toLowerCase()
               return code !== 'PR' && name.indexOf('puerto') === -1
            })

            // clear selection if the current add-selection was PR
            if (this.selectedState) {
               const sc = (this.selectedState.stateCode || '').toUpperCase()
               const sn = (this.selectedState.stateName || '').toLowerCase()
               if (sc === 'PR' || sn.indexOf('puerto') !== -1) {
                  this.selectedState = null
                  this.showAddRow = false
               }
            }

            // clear CC state if it is PR
            if (this.ccInfo && (this.ccInfo.stateCode || '').toUpperCase() === 'PR') {
               this.ccInfo.stateCode = null
            }

            // touch validation and recalc fees, notify parent
            if (this.v$ && this.v$.states) this.v$.states.$touch()
            this.calcFees()
            this.chg()
         },
         stateFeeValid() {
            return (!this.getAnswer(this.stateFeeQuestion.questionId).answerBoolean || (this.isPAFee || this.isMTFee));
         },
         stateFeeAnswered() {
            return this.getAnswer(this.stateFeeQuestion.questionId).answerBoolean != null;
         },

         // datalist input handlers
         onStateInput() {
            // If user typed a name that exactly matches a known state name, set the code.
            const match = this.allStates.find(s => s.name.toLowerCase() === (this.ccStateSearch || '').trim().toLowerCase())
            if (match) {
               this.ccInfo.stateCode = match.code
            } else {
               // clear the code until the user picks a valid value
               this.ccInfo.stateCode = null
            }
            this.chg()
         },
         onStateBlur() {
            // Validate/touch on blur
            this.v$.ccInfo.stateCode.$touch()
            // If the user entered a partial name that matches exactly one state prefix, keep code for UX:
            if (!this.ccInfo.stateCode && this.ccStateSearch) {
               const q = this.ccStateSearch.trim().toLowerCase()
               const matches = this.allStates.filter(s => s.name.toLowerCase().startsWith(q))
               if (matches.length === 1) {
                  this.ccInfo.stateCode = matches[0].code
                  this.ccStateSearch = matches[0].name
                  this.chg()
               }
            }
         }
      },
      computed: {
         // effective flag: prop OR the Puerto Rico 2nd-question answer set to true
         effectiveAllowPuertoRico() {

            // otherwise, if the secondary PR question exists and its answerBoolean is true, allow PR
            try {
               if (this.puertoRico2ndQuestion && this.puertoRico2ndQuestion.questionId) {
                  const ans = this.getPRAnswer(this.puertoRico2ndQuestion.questionId);
                  return ans && ans.answerBoolean === true;
               }
            } catch (e) {
               // defensive: return prop value
            }
            return false;
         },
         filteredFees() {
            const q = (this.stateSearch || '').trim().toLowerCase()
            const base = this.fees || []

            const allowed = base.filter(f => {
               // Try to find matching allStates entry using several fallbacks:
               // 1) exact stateCode on fee
               // 2) matching stateName on fee
               // comparisons are case-insensitive
               const feeCode = (f.stateCode || '').toString().trim().toLowerCase()
               const feeName = (f.stateName || '').toString().trim().toLowerCase()

               const st = this.allStates.find(a => {
                  if (!a) return false
                  const ac = (a.code || '').toString().trim().toLowerCase()
                  const an = (a.name || '').toString().trim().toLowerCase()
                  return (ac && feeCode && ac === feeCode) || (an && feeName && an === feeName) || (ac && ac === feeName)
               })

               // If matched state entry exists and it's Puerto Rico, check the flag
               if (st && st.code === 'PR') {
                  // show Puerto Rico if either the per-state flag allows it or the PR second-question enables it
                  return (st.effectiveAllowPuertoRico !== false) || this.effectiveAllowPuertoRico
               }

               // If matched state entry exists and defines a custom allow flag, honor it
               if (st && typeof st.effectiveAllow !== 'undefined') {
                  return !!st.effectiveAllow
               }

               // default: allow the fee
               return true
            })

            if (!q) return allowed

            return allowed.filter(f =>
               (f.stateName || '').toString().toLowerCase().startsWith(q) ||
               (f.stateCode || '').toString().toLowerCase().startsWith(q)
            )
         }
   }
};
</script>

<style scoped lang="scss">
@import "../../assets/css/colors.scss";
@import "../../assets/css/application.scss";
@import "../../assets/css/validation.scss";

.add-state-link {
   display:block;
   color: #fff;
   font-size: 0.7em;
   line-height: 0.7em;
   padding: 6px 6px 3px 12px;
}
.add-state-link:hover {
   font-weight: normal;
   color: #000;
}
.table button {
   padding: 0 6px;
   border-width: 0;
   background-color: transparent;
}
.table td {
   border-top: unset;
}
   .add-state-btn ul {
      list-style: none;
   }
   .add-state-btn .the-button {
      width: 100%;
      text-align: left;
      background-color: #fff;
      border: 1px solid #ddd;
      height: 28px;
      border-radius: 0px;
   }
   .add-state-btn .the-button:focus, .add-state-btn .the-button:active {
      outline: none !important;
      box-shadow: none !important;
   }
   .add-state-btn .dropdown-toggle::after {
      content: none;
   }
   .add-state-btn .fa-caret-down {
      margin-top: 3px;
      float: right;
      text-align:right;
   }
   .add-state-btn .dropdown-item i, button i:first-child {
      font-size: 1.3em;
      padding-right: 6px;
      min-width: 34px;
   }
   .add-state-btn a:hover {
      font-weight: normal;
   } 
   .add-state-btn .dropdown-menu {
      height:200px;
      overflow-y:auto;
   }
.err-box {
   background-color: #ffcccc;
   border: 1px solid #ff0000;
   border-radius: 4px;
   padding: 12px;
   display:inline-block;
}
.alert-warning {
   color: red;
}
.alert i {
   color: red;
   font-size: larger;
   padding-right: 12px;
}

   /* Styles for the searchable state control */
   .cc-state-wrapper {
      position: relative;
   }

   .state-suggestions {
      position: absolute;
      z-index: 2000;
      left: 0;
      right: 0;
      max-height: 200px;
      overflow-y: auto;
      background: #fff;
      margin-top: 2px;
      padding-left: 0;
      list-style: none;
      border: 1px solid #ddd;
      border-radius: 2px;
   }

   .state-suggestion-item a {
      display: block;
      padding: 8px 12px;
      color: #333;
      text-decoration: none;
   }

   .state-suggestion-item a:hover {
      background: #f1f1f1;
   }
   /* custom fees dropdown */
   .add-state-btn.custom-dd {
      position: relative;
      display: block;
   }

   .add-state-btn.custom-dd .the-button {
      padding-right: 28px;
   }

   .add-state-btn.custom-dd .the-button {
      position: relative;
      display: block;
      width: 100%;
      text-align: left;
      background-color: #fff;
      border: 1px solid #ddd;
      height: 34px; /* match visual height */
      line-height: 22px;
      padding: 6px 36px 6px 12px; /* leave room for absolute caret on right */
      border-radius: 0;
      box-sizing: border-box;
      cursor: pointer;
   }

   /* absolute caret so it stays vertically centered and doesn't affect layout */
   .add-state-btn.custom-dd .the-button .fa-caret-down,
   .add-state-btn.custom-dd .the-button .fas.fa-caret-down {
      position: absolute;
      right: 10px;
      top: 50%;
      transform: translateY(-50%);
      pointer-events: none;
      color: #333;
      font-size: 0.9em;
   }

   /* optional: small visual tweak when menu open */
   .add-state-btn.custom-dd .the-button:focus,
   .add-state-btn.custom-dd .the-button:active {
      outline: none;
      box-shadow: none;
   }

   /* Constrain menu height and enable vertical scroll */
   .fees-menu {
      position: absolute;
      z-index: 3000;
      left: 0;
      right: 0;
      background: #fff;
      border: 1px solid #ddd;
      border-radius: 2px;
      margin-top: 6px;
      max-height: 360px; /* limit height */
      overflow-y: auto; /* show scrollbar when content overflows */
      box-shadow: 0 6px 18px rgba(0,0,0,0.08);
   }

   /* Let the list flow inside the constrained menu */
   .fees-list {
      margin: 0;
      padding: 0;
      max-height: none;
      overflow: visible;
   }

   .state-name-wrap {
      white-space: normal; /* allow wrapping */
      overflow-wrap: anywhere; 
      word-break: break-word;
      line-height: 1.2;
   }
</style>